import * as React from 'react';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import classNames from 'classnames';
import { Tooltip, Text } from '@box/blueprint-web';
import { ComboboxWithApiPagination } from '@box/metadata-editor';
import useId from '../../utils/useId';
import messages from './messages';
import css from './taxonomy-field.module.scss';
import { BuilderInlineError } from '../../../components/new-file-request-builder/new-form-element/builder-inline-error';

type TaxonomyFieldProps = {
  className?: string;
  description?: React.ReactNode;
  error?: React.ReactNode | string;
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  fetcher: any;
  infoText?: React.ReactNode | string;
  isDisabled?: boolean;
  isRequired?: boolean;
  label: React.ReactNode;
  onChange: (...args: unknown[]) => unknown;
  optionsRules?: {
    multiSelect: boolean;
    selectableLevels: number[];
  };
  value: string[];
  isSubmitter?: boolean;
};

type Props = TaxonomyFieldProps & {
  intl: IntlShape;
};

const TaxonomyField = (props: Props) => {
  const {
    className,
    description,
    error,
    fetcher,
    infoText,
    isDisabled,
    isRequired,
    label,
    optionsRules: { multiSelect } = {},
    onChange,
    value,
    intl,
    isSubmitter,
  } = props;

  const loadingAriaLabel = intl.formatMessage(
    messages.taxonomyFieldLoadingAriaLabel,
  );
  const placeholder = multiSelect
    ? intl.formatMessage(messages.taxonomyFieldMultiSelectPlaceholderMessage)
    : intl.formatMessage(messages.taxonomyFieldSingleSelectPlaceholderMessage);

  const descriptionID = useId('description');
  const labelID = useId('labelID');
  const ariaAttrs = {
    'aria-describedby': description ? descriptionID : undefined,
    'aria-labelledby': labelID,
  } as const;

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  const handleChange = (newValue: any) => {
    onChange(newValue);
  };

  return (
    <div className={classNames(className, css.blueprintSelectContainer)}>
      <Text
        id={labelID}
        as="label"
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2820
        color="text-on-light-default"
        variant="subtitle"
        className={classNames({ [css.label]: isSubmitter })}
      >
        {label}
      </Text>
      <Tooltip
        content={infoText}
        // Hide tooltip when no info text provided
        open={infoText ? undefined : false}
        side="right"
        variant="standard"
      >
        <ComboboxWithApiPagination
          as="input"
          data-testid="blueprint-taxonomy-field"
          defaultFetcher={fetcher}
          disabled={isDisabled}
          error={error}
          loadingAriaLabel={loadingAriaLabel}
          multiselect={multiSelect}
          onValueChange={handleChange}
          placeholder={placeholder}
          required={isRequired}
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          value={value}
          {...ariaAttrs}
        />
      </Tooltip>

      {description && (
        <Text
          as="span"
          className={css.blueprintDescriptionText}
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          color="textOnLight"
          id={descriptionID}
          variant="bodyDefault"
        >
          {description}
        </Text>
      )}

      <BuilderInlineError />
    </div>
  );
};

TaxonomyField.displayName = 'TaxonomyField';

export { TaxonomyField };
type WrappedProps = Omit<Props, 'intl'>;
const Wrapped = injectIntl(TaxonomyField);
export default Wrapped as React.ComponentType<WrappedProps>;
